import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Lock, Check, Eye, X, Loader2, Sparkles, Download, FolderArchive } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

/**
 * TemplateGallery - "Choose Your Resume Design".
 * Renders the user's ACTUAL aligned resume through each of the six premium
 * design systems. Free plans get Professional + Minimal; the rest are
 * previewable but export-gated behind a paid plan.
 */

const PAGE_W = 816; // 8.5in @ 96dpi
const PAGE_H = 1056; // 11in @ 96dpi

function ScaledPreview({ html, error, onRetry, className = "", maxPages = 1 }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.3);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const compute = () => setScale(el.clientWidth / PAGE_W);
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={wrapRef} className={`relative overflow-hidden ${className}`}
         style={{ height: PAGE_H * scale * maxPages * 0.62 }}>
      {html ? (
        <iframe
          title="resume preview"
          srcDoc={html}
          scrolling="no"
          style={{
            width: PAGE_W, height: PAGE_H * maxPages, border: "0",
            transform: `scale(${scale})`, transformOrigin: "top left",
            pointerEvents: "none", background: "#fff",
          }}
        />
      ) : error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-900/60 text-center px-3">
          <span className="text-[11px] text-zinc-500">Preview unavailable</span>
          <button type="button" data-testid="template-preview-retry"
                  onClick={(e) => { e.stopPropagation(); onRetry?.(); }}
                  className="text-[11px] text-amber-400 hover:text-amber-300 underline underline-offset-2">
            Retry
          </button>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/60">
          <Loader2 className="h-5 w-5 text-zinc-600 animate-spin" />
        </div>
      )}
    </div>
  );
}

function PreviewModal({ template, templates, html, onSwitch, onUse, onUnlock, onClose, using }) {
  return createPortal(
    <div data-testid="template-preview-modal" className="fixed inset-0 z-[80] flex flex-col bg-zinc-950/90 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-zinc-800 bg-zinc-950/95">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400">Preview your resume</div>
          <div className="text-sm font-semibold text-zinc-100 truncate">{template.name}, {template.tagline}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {template.locked ? (
            <Link to="/app/upgrade" data-testid="template-preview-upgrade"
                  onClick={() => onUnlock?.(template.id, "modal")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 text-xs transition-colors">
              <Lock className="h-3.5 w-3.5" /> Unlock with Pro
            </Link>
          ) : (
            <button type="button" data-testid="template-preview-use"
                    onClick={() => onUse(template.id)} disabled={using}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-4 py-2 text-xs transition-colors disabled:opacity-60">
              {using ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Use This Template
            </button>
          )}
          <button type="button" data-testid="template-preview-close" onClick={onClose}
                  className="h-8 w-8 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center text-zinc-400">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex gap-2 px-4 sm:px-6 py-2.5 border-b border-zinc-800/70 overflow-x-auto">
        {templates.map((t) => (
          <button key={t.id} type="button" data-testid={`template-switch-${t.id}`}
                  onClick={() => onSwitch(t.id)}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] border transition-colors ${
                    t.id === template.id
                      ? "bg-amber-500/15 border-amber-500/50 text-amber-300"
                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                  }`}>
            {t.locked && <Lock className="h-3 w-3" />}{t.name}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto py-6 px-3">
        <div className="max-w-[860px] mx-auto rounded-lg overflow-hidden shadow-2xl">
          {html ? (
            <iframe title="full resume preview" srcDoc={html}
                    className="w-full bg-white border-0"
                    style={{ height: "calc(100vh - 160px)", minHeight: 700 }} />
          ) : (
            <div className="h-[70vh] flex items-center justify-center bg-zinc-900">
              <Loader2 className="h-6 w-6 text-zinc-600 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function TemplateGallery({ alignedId, initialTemplateId, onTemplateSelected }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(initialTemplateId || "");
  const [previews, setPreviews] = useState({}); // template_id -> html
  const [failed, setFailed] = useState({}); // template_id -> true
  const [previewingId, setPreviewingId] = useState(null);
  const [using, setUsing] = useState(false);
  const [pkgInfo, setPkgInfo] = useState(null); // {has_cover_letter}

  const fetchPreview = useCallback(async (tid) => {
    setFailed((f) => ({ ...f, [tid]: false }));
    try {
      const r = await api.get(`/resume/aligned/${alignedId}/preview`, {
        params: { template_id: tid }, responseType: "text",
        transformResponse: [(x) => x],
      });
      setPreviews((p) => ({ ...p, [tid]: r.data }));
    } catch (e) {
      console.warn("[templates] preview failed", tid, e);
      setFailed((f) => ({ ...f, [tid]: true }));
    }
  }, [alignedId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/resume/templates");
        if (!alive) return;
        setTemplates(data.templates || []);
        setSelectedId((cur) => cur || initialTemplateId || data.default_template);
        track("templates.gallery_viewed", { aligned_id: alignedId, is_paid: data.is_paid });
        (data.templates || []).forEach((t) => fetchPreview(t.id));
      } catch (e) {
        console.warn("[templates] list failed", e);
      }
      try {
        const { data: pk } = await api.get(`/resume/aligned/${alignedId}/package-info`);
        if (alive) setPkgInfo(pk);
      } catch (e) {
        console.warn("[templates] package info failed", e);
      }
    })();
    return () => { alive = false; };
  }, [alignedId, initialTemplateId, fetchPreview]);

  const applyTemplate = useCallback(async (tid) => {
    setUsing(true);
    try {
      await api.post(`/resume/aligned/${alignedId}/template`, { template_id: tid });
      setSelectedId(tid);
      onTemplateSelected?.(tid);
      setPreviewingId(null);
      track("templates.selected", { template_id: tid, aligned_id: alignedId });
      const name = templates.find((t) => t.id === tid)?.name || tid;
      toast.success(`${name} selected. Your downloads now use this design.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not select this template.");
    } finally {
      setUsing(false);
    }
  }, [alignedId, onTemplateSelected, templates]);

  if (!templates.length) return null;
  const previewing = templates.find((t) => t.id === previewingId);

  const openPreview = (t) => {
    setPreviewingId(t.id);
    track("templates.previewed", { template_id: t.id, locked: !!t.locked });
  };
  const trackUnlock = (tid, source) => track("templates.unlock_clicked", { template_id: tid, source });

  return (
    <div data-testid="template-gallery" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Premium designs
          </div>
          <h3 className="text-base font-semibold text-zinc-100 mt-1">Choose Your Resume Design</h3>
          <p className="text-xs text-zinc-400 mt-0.5">Your content is ready. Now choose how you want it to look. Previews use your actual optimized resume.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => {
          const isSelected = t.id === selectedId;
          return (
            <div key={t.id} data-testid={`template-card-${t.id}`}
                 className={`group rounded-xl border overflow-hidden transition-colors ${
                   isSelected ? "border-emerald-500/60 bg-emerald-500/[0.04]" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-600"
                 }`}>
              <button type="button" onClick={() => openPreview(t)}
                      className="block w-full text-left cursor-zoom-in relative">
                <ScaledPreview html={previews[t.id]} error={failed[t.id]} onRetry={() => fetchPreview(t.id)} />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-950/90 border border-zinc-700 text-zinc-100 text-[11px] px-3 py-1.5">
                    <Eye className="h-3 w-3" /> Preview
                  </span>
                </div>
                {isSelected && (
                  <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-emerald-950" />
                  </span>
                )}
              </button>
              <div className="p-3 border-t border-zinc-800/70">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-zinc-100">{t.name}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] uppercase tracking-wider rounded-full border border-zinc-700 text-zinc-400 px-2 py-0.5">{t.ats}</span>
                    {t.locked && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 px-2 py-0.5">
                        <Lock className="h-2.5 w-2.5" /> Pro
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{t.tagline}</div>
                <div className="mt-2.5 flex items-center gap-2">
                  <button type="button" data-testid={`template-preview-btn-${t.id}`}
                          onClick={() => openPreview(t)}
                          className="flex-1 rounded-lg border border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-[11px] font-medium px-2 py-1.5 transition-colors">
                    Preview
                  </button>
                  {t.locked ? (
                    <Link to="/app/upgrade" data-testid={`template-unlock-${t.id}`}
                          onClick={() => trackUnlock(t.id, "card")}
                          className="flex-1 text-center rounded-lg bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-300 text-[11px] font-semibold px-2 py-1.5 transition-colors">
                      Unlock
                    </Link>
                  ) : (
                    <button type="button" data-testid={`template-use-${t.id}`}
                            onClick={() => applyTemplate(t.id)} disabled={using || isSelected}
                            className={`flex-1 rounded-lg text-[11px] font-semibold px-2 py-1.5 transition-colors ${
                              isSelected
                                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 cursor-default"
                                : "bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
                            }`}>
                      {isSelected ? "Selected" : "Use This Template"}
                    </button>
                  )}
                </div>
                {isSelected && (
                  <div className="mt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        data-testid={`template-download-pdf-${t.id}`}
                        href={`${API_BASE}/resume/aligned/${alignedId}/download?format=pdf&template_id=${t.id}`}
                        onClick={() => track("templates.download", { format: "pdf", template_id: t.id, aligned_id: alignedId, source: "gallery" })}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-zinc-100 hover:bg-white text-zinc-900 text-[11px] font-semibold px-2 py-1.5 transition-colors"
                      >
                        <Download className="h-3 w-3" /> PDF
                      </a>
                      <a
                        data-testid={`template-download-docx-${t.id}`}
                        href={`${API_BASE}/resume/aligned/${alignedId}/download?format=docx&template_id=${t.id}`}
                        onClick={() => track("templates.download", { format: "docx", template_id: t.id, aligned_id: alignedId, source: "gallery" })}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-600 hover:bg-zinc-800 text-zinc-100 text-[11px] font-semibold px-2 py-1.5 transition-colors"
                      >
                        <Download className="h-3 w-3" /> DOCX
                      </a>
                    </div>
                    <a
                      data-testid={`template-download-package-${t.id}`}
                      href={`${API_BASE}/resume/aligned/${alignedId}/package?template_id=${t.id}`}
                      onClick={() => track("templates.package_download", { template_id: t.id, aligned_id: alignedId, has_letter: !!pkgInfo?.has_cover_letter })}
                      className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-300 text-[11px] font-semibold px-2 py-1.5 transition-colors"
                    >
                      <FolderArchive className="h-3 w-3" /> Application Package (ZIP)
                    </a>
                    {pkgInfo && !pkgInfo.has_cover_letter && (
                      <div data-testid="package-no-letter-note" className="text-[10px] text-zinc-500 mt-1 leading-snug">
                        Resume only for now. Draft a cover letter for this job to include it in the package.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {previewing && (
        <PreviewModal
          template={previewing}
          templates={templates}
          html={previews[previewing.id]}
          onSwitch={(tid) => { const t = templates.find((x) => x.id === tid); if (t) openPreview(t); }}
          onUse={applyTemplate}
          onUnlock={trackUnlock}
          onClose={() => setPreviewingId(null)}
          using={using}
        />
      )}
    </div>
  );
}
